import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import { toApiError } from '@/api/client';

export const Route = createFileRoute('/_authenticated/admin/buscar/')({
  component: AdminSearchPage,
});

function AdminSearchPage(): JSX.Element {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');

  // Debounce 300ms para no pegarle al backend en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setQ(raw.trim()), 300);
    return () => clearTimeout(t);
  }, [raw]);

  const enabled = q.length >= 2;
  const searchQ = useQuery({
    queryKey: ['admin-search', q],
    queryFn: () => adminApi.searchAdmin(q),
    enabled,
    placeholderData: keepPreviousData,
  });

  const tenants = searchQ.data?.tenants ?? [];
  const users = searchQ.data?.users ?? [];
  const empty = enabled && !searchQ.isFetching && tenants.length === 0 && users.length === 0;

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search color="primary" /> Buscar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Encontrá una cuenta o usuario por nombre o email, en todos los tenants.
        </Typography>
      </Stack>

      <TextField
        autoFocus
        fullWidth
        placeholder="Nombre o email… (mín. 2 caracteres)"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searchQ.isFetching ? <CircularProgress size={18} /> : <Search fontSize="small" />}
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {searchQ.isError ? (
        <Alert severity="error">{toApiError(searchQ.error).message}</Alert>
      ) : !enabled ? (
        <Typography variant="body2" color="text.secondary">
          Escribí al menos 2 caracteres.
        </Typography>
      ) : empty ? (
        <Alert severity="info">Sin resultados para “{q}”.</Alert>
      ) : (
        <Stack spacing={2}>
          {tenants.length > 0 ? (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Tenants ({tenants.length})
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {tenants.map((t) => (
                    <ListItemButton
                      key={t.id}
                      onClick={() => navigate({ to: '/admin/tenants/$id', params: { id: t.id } })}
                    >
                      <ListItemText primary={t.name} secondary={t.email} />
                      <Chip
                        size="small"
                        color={t.status === 'Active' ? 'success' : t.status === 'Suspended' ? 'warning' : 'default'}
                        variant="outlined"
                        label={t.status}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Box>
          ) : null}

          {users.length > 0 ? (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Usuarios ({users.length})
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {users.map((u) => (
                    <ListItemButton
                      key={u.id}
                      onClick={() => navigate({ to: '/admin/tenants/$id', params: { id: u.tenantId } })}
                    >
                      <ListItemText
                        primary={u.email}
                        secondary={`${u.name} · ${u.role} · tenant ${u.tenantId.slice(0, 8)}…`}
                      />
                      <Chip
                        size="small"
                        color={u.status === 'Active' ? 'success' : u.status === 'Disabled' ? 'default' : 'warning'}
                        variant="outlined"
                        label={u.status}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
